import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { EvolveHeader } from "@/components/EvolveHeader";
import { BottomNav } from "@/components/BottomNav";
import { ChatTab } from "@/components/tabs/ChatTab";
import { ProgressTab } from "@/components/tabs/ProgressTab";
import { ProfileTab } from "@/components/tabs/ProfileTab";
import { AddPRModal } from "@/components/AddPRModal";
import { Onboarding } from "@/components/Onboarding";
import { SettingsModal } from "@/components/SettingsModal";
import { NotificationPrompt } from "@/components/NotificationPrompt";
import { PaywallModal } from "@/components/PaywallModal";
import { WorkoutSession } from "@/components/WorkoutSession";
import { LeaderboardModal } from "@/components/LeaderboardModal";
import { OnboardingTour } from "@/components/OnboardingTour";
import { ReferralModal } from "@/components/ReferralModal";
import { ResumeWorkoutBanner } from "@/components/ResumeWorkoutBanner";
import { PremiumConfetti } from "@/components/PremiumConfetti";

import { useToast } from "@/hooks/use-toast";
import { useEvolveChat } from "@/hooks/useEvolveChat";
import { useAuth } from "@/hooks/useAuth";
import { useProfile, ExtendedProfile } from "@/hooks/useProfile";
import { useTheme } from "@/hooks/useTheme";
import { useChatHistory } from "@/hooks/useChatHistory";
import { useWeightHistory } from "@/hooks/useWeightHistory";
import { useWorkoutSessions } from "@/hooks/useWorkoutSessions";
import { useOneSignalNotifications } from "@/hooks/useOneSignalNotifications";
import { useScheduledNotifications } from "@/hooks/useScheduledNotifications";
import { useWorkoutReminders } from "@/hooks/useWorkoutReminders";
import { useSubscription } from "@/hooks/useSubscription";
import { usePlatformPurchase } from "@/hooks/usePlatformPurchase";
import { useReferralNotifications } from "@/hooks/useReferralNotifications";
import { useBadges } from "@/hooks/useBadges";
import { useHealthKit } from "@/hooks/useHealthKit";
import { useNutritionLogs } from "@/hooks/useNutritionLogs";
import { useAIConsent } from "@/hooks/useAIConsent";
import { usePromptQueue } from "@/hooks/usePromptQueue";
import { ReferralNotificationModal } from "@/components/ReferralNotificationModal";
import { HealthConnectionPrompt } from "@/components/HealthConnectionPrompt";
import { BadgeUnlockModal } from "@/components/BadgeUnlockModal";
import type { ChatMessage, PR, CoachingStyle, UserProfile, EvolveTier, AppTheme } from "@/types/evolve";
import { getPersonalizedInitialMessage, calculateTier } from "@/types/evolve";
import { TierUpCelebration } from "@/components/TierUpCelebration";
import { Loader2 } from "lucide-react";
import { PricingPlan } from "@/config/stripe";
import { findPRsToSync } from "@/utils/prDetection";
import { detectWorkoutFromMessage, formatDetectedWorkout } from "@/utils/workoutDetection";
import { detectWeightFromMessage, isWeightChanged } from "@/utils/weightDetection";
import { detectNutritionFromConversation, detectNutritionFromUserMessage } from "@/utils/nutritionDetection";
import type { WorkoutSession as WorkoutSessionType } from "@/types/workout";
import { Badge } from "@/types/badges";
import { PremiumStatusModal } from "@/components/PremiumStatusModal";
import { AIConsentModal } from "@/components/AIConsentModal";

// Exercise names to identify actual workout plans
const EXERCISE_NAMES = [
  'squat', 'deadlift', 'bench', 'press', 'curl', 'row', 'pull-up', 'push-up',
  'lunges', 'plank', 'dips', 'fly', 'extension', 'raise', 'shrug', 'pulldown',
  'leg press', 'calf', 'crunch', 'sit-up', 'hip thrust'
];

const DAILY_MESSAGE_LIMIT = 10;
const TRIAL_DAYS = 3;

export default function Index() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, loading: authLoading, signOut, isEmailVerified } = useAuth();
  const { profile, loading: profileLoading, error: profileError, onboardingComplete, updateProfile, refetch: refetchProfile } = useProfile(user?.id);
  const { theme, updateTheme, resetTheme } = useTheme(user?.id);
  const { 
    messages, 
    setMessages, 
    saveMessage, 
    updateMessage,
    searchMessages,
    loadMore,
    hasMore,
    loading: chatHistoryLoading 
  } = useChatHistory(user?.id);
  const { 
    isEnabled: notificationsEnabled, 
    requestPermission: requestNotifications,
    shouldPromptForPermission: shouldPromptNotifications,
    updateUserTags,
  } = useOneSignalNotifications();
  
  const { weightHistory, logWeight, deleteWeightEntry, importFromHealth } = useWeightHistory(user?.id);
  const { sessions: workoutSessions, totalWorkouts, logWorkout } = useWorkoutSessions(user?.id);
  const { 
    todaySummary: todayNutrition, 
    weekSummary: weekNutrition, 
    logNutrition,
    deleteLog: deleteNutritionLog,
  } = useNutritionLogs(user?.id);
  const { subscription, createCheckout, openCustomerPortal, loading: subscriptionLoading, checkSubscription } = useSubscription();
  const { purchase: platformPurchase, loading: platformPurchaseLoading } = usePlatformPurchase();
  const { currentNotification, dismissCurrent } = useReferralNotifications();
  const { 
    newlyUnlocked, 
    isBadgeUnlocked, 
    getBadgeUnlockDate, 
    dismissUnlock,
    checkBadges 
  } = useBadges({
    userId: user?.id,
    daysActive: profile.daysActive,
    totalWorkouts,
    prs: profile.prs,
    isPremium: profile.isPremium,
  });
  const [activeTab, setActiveTab] = useState<'chat' | 'progress' | 'profile'>('chat');
  const [isLoading, setIsLoading] = useState(false);
  const [messagesUsed, setMessagesUsed] = useState(0);
  const [showPRModal, setShowPRModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [userMotivation, setUserMotivation] = useState<string | undefined>();
  const [showWorkoutSession, setShowWorkoutSession] = useState(false);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showReferralModal, setShowReferralModal] = useState(false);
  const [selectedBadge, setSelectedBadge] = useState<Badge | null>(null);
  const [tourHighlightedCard, setTourHighlightedCard] = useState<'workout' | 'nutrition' | null>(null);
  const [showTierCelebration, setShowTierCelebration] = useState(false);
  const [previousTier, setPreviousTier] = useState<EvolveTier | null>(null);
  const [celebrationTier, setCelebrationTier] = useState<EvolveTier>('Seed');
  const [showPremiumConfetti, setShowPremiumConfetti] = useState(false);
  const [showPremiumStatusModal, setShowPremiumStatusModal] = useState(false);
  
  // AI consent hook - must be granted before any AI features
  const {
    hasConsent: hasAIConsent,
    isLoading: aiConsentLoading,
    consentDeniedMessage,
    needsConsentPrompt,
    requestConsent: requestAIConsent,
    grantConsent: grantAIConsent,
    declineConsent: declineAIConsent,
    dismissDeniedMessage,
    clearConsentPrompt,
    canUseAI,
  } = useAIConsent();
  
  const [currentWorkoutPlan, setCurrentWorkoutPlan] = useState<string>('');
  const [editingPR, setEditingPR] = useState<PR | null>(null);
  const [preselectedLift, setPreselectedLift] = useState<string | null>(null);
  const [pendingAutoMessage, setPendingAutoMessage] = useState<string | null>(null);

  // Health integration
  const { 
    isNativeApp: isHealthAvailable, 
    status: healthStatus,
    syncWorkoutToHealth,
    syncWeightToHealth,
    syncNutritionToHealth,
    readSteps,
    readHeartRate,
    readActiveEnergy,
    readWeight: readWeightFromHealth,
    platformName,
  } = useHealthKit();

  // Determine which prompts should be shown (conditions only, not display logic)
  // Must come after all hooks that provide the condition data
  const shouldShowAIConsent = onboardingComplete && hasAIConsent === false && !aiConsentLoading;
  const shouldShowTour = onboardingComplete && !profile.hasSeenTour && !profileLoading && 
    sessionStorage.getItem('evolve_tour_completed_session') !== 'true';
  
  // Health prompt - show ONCE on first launch, if denied, NEVER show again automatically
  // User can still manually connect via Settings → Connect Apple Health
  const healthPermanentlyDenied = localStorage.getItem('health_permanently_denied') === 'true';
  const healthAlreadyPrompted = localStorage.getItem('health_prompted_onboarding') === 'true';
  const shouldShowHealthOnboarding = onboardingComplete && 
    isHealthAvailable && 
    !healthStatus.isAuthorized && 
    !healthPermanentlyDenied && 
    !healthAlreadyPrompted &&
    !profileLoading;
  const shouldShowHealthFirstWorkout = false; // Disabled - only show on first launch
  
  // Notification prompt conditions
  const shouldShowNotificationPromptCondition = onboardingComplete && shouldPromptNotifications() && 
    localStorage.getItem('pending_notification_prompt') !== 'true';

  // Unified prompt queue - ensures only one prompt shows at a time
  const {
    isPromptActive,
    dismissPrompt,
    forceShowPrompt,
    hasActivePrompt,
  } = usePromptQueue({
    shouldShowAIConsent,
    shouldShowTour,
    shouldShowHealthOnboarding,
    shouldShowHealthFirstWorkout,
    shouldShowNotifications: shouldShowNotificationPromptCondition,
    isLoading: authLoading || profileLoading || aiConsentLoading,
  });

  // Auto-enable healthSyncEnabled when HealthKit authorization is granted
  // This ensures the Progress tab's Health Sync section updates immediately
  useEffect(() => {
    if (healthStatus.isAuthorized && !profile.healthSyncEnabled && user?.id && !profileLoading) {
      console.log('[Health] Auto-enabling healthSyncEnabled after authorization');
      updateProfile({ healthSyncEnabled: true });
    }
  }, [healthStatus.isAuthorized, profile.healthSyncEnabled, user?.id, profileLoading]);

  // Extract workout plan from messages - require actual exercise tables
  const workoutPlan = useMemo(() => {
    const aiMessages = messages.filter(m => m.isAI).reverse();
    for (const message of aiMessages) {
      const lowerText = message.text.toLowerCase();
      
      // Must have table structure: header row with | separators
      const hasTableRow = /\|[^|]+\|[^|]+\|/.test(message.text);
      
      // Must have sets/reps pattern (like "3x10" or "4 x 8" or "3 sets")
      const hasSetsReps = /\d+\s*[x×]\s*\d+|\d+\s*sets/i.test(message.text);
      
      // Must mention actual exercises in workout context
      const exerciseCount = EXERCISE_NAMES.filter(exercise => lowerText.includes(exercise)).length;
      
      // Must have Day headers (Day 1, Day 2, etc.)
      const hasDayHeaders = /day\s*\d+[:\s\-]/i.test(message.text);
      
      // Stricter: require table rows + day headers + sets/reps + multiple exercises
      if (hasTableRow && hasDayHeaders && hasSetsReps && exerciseCount >= 2 && message.text.length > 300) {
        console.log('[WorkoutPlan] Found valid workout plan with', exerciseCount, 'exercises');
        return message.text;
      }
    }
    console.log('[WorkoutPlan] No valid workout plan found in messages');
    return null;
  }, [messages]);

  // Set up scheduled daily motivational notifications
  useScheduledNotifications({
    userName: profile.name,
    motivation: userMotivation,
  });

  // Set up workout reminders (1.5hr and 2hr after starting)
  const { 
    startWorkoutTracking, 
    completeWorkoutTracking 
  } = useWorkoutReminders({
    userName: profile.name,
  });

  // Note: Auth redirect is now handled by ProtectedRoute wrapper in App.tsx

  // Handle pending notification prompt from settings (force re-prompt)
  useEffect(() => {
    const pendingPrompt = localStorage.getItem('pending_notification_prompt');
    if (pendingPrompt === 'true') {
      localStorage.removeItem('pending_notification_prompt');
      
      // FORCE a permission attempt - this bypasses the denied check
      setTimeout(async () => {
        const enabled = await requestNotifications(true);
        
        if (enabled) {
          toast({
            title: "🔔 Notifications Enabled!",
            description: "You're 4.7× more likely to hit Immortal now.",
          });
        } else {
          toast({
            title: "Still Blocked",
            description: "The permission is still blocked. Make sure to change Notifications to 'Ask' (not just 'Allow'), then try again.",
            variant: "destructive",
          });
        }
      }, 500);
    }
  }, [requestNotifications]);

  // Notification prompt is now controlled by prompt queue - no separate auto-show effect needed

  // Tour is now controlled by prompt queue - no separate effect needed
  // The handleTourComplete function still handles saving to database

  // Track tier changes for celebration - use database to track last celebrated tier
  // Only triggers when tier ACTUALLY increases from a workout, not on login
  const previousTotalWorkoutsRef = useRef<number | null>(null);
  const hasInitializedTierRef = useRef(false);
  
  // Use sessionStorage to persist tier initialization across component remounts
  const SESSION_TIER_KEY = 'evolve_tier_initialized';
  
  useEffect(() => {
    if (!user?.id || profileLoading) return;
    
    const { currentTier } = calculateTier(totalWorkouts);
    const tierOrder: EvolveTier[] = ['Seed', 'Spark', 'Flame', 'Ember', 'Blaze', 'Inferno', 'Pre-Legend', 'Legend', 'Titan', 'Immortal', 'Immortal II', 'Immortal III', 'Ascended', 'Ascended II', 'Mythic', 'Eternal'];
    const currentTierIndex = tierOrder.indexOf(currentTier);
    const lastCelebratedTier = profile.lastCelebratedTier;
    const lastCelebratedIndex = lastCelebratedTier ? tierOrder.indexOf(lastCelebratedTier as EvolveTier) : -1;
    
    // Check both ref AND sessionStorage to handle component remounts
    const sessionInitialized = sessionStorage.getItem(SESSION_TIER_KEY) === 'true';
    
    // First load - initialize refs and database, NO celebration
    if (!hasInitializedTierRef.current && !sessionInitialized) {
      hasInitializedTierRef.current = true;
      sessionStorage.setItem(SESSION_TIER_KEY, 'true');
      previousTotalWorkoutsRef.current = totalWorkouts;
      setPreviousTier(currentTier);
      
      // If no tier was ever celebrated, set current tier as celebrated to prevent future false celebrations
      if (!lastCelebratedTier) {
        updateProfile({ lastCelebratedTier: currentTier });
      }
      return; // Exit early - never celebrate on initial load
    }
    
    // If session already initialized but ref not set (component remounted), sync the ref
    if (sessionInitialized && !hasInitializedTierRef.current) {
      hasInitializedTierRef.current = true;
      previousTotalWorkoutsRef.current = totalWorkouts;
      setPreviousTier(currentTier);
      return; // Don't celebrate on remount either
    }
    
    // Only show celebration if:
    // 1. Workouts actually increased (user completed a workout)
    // 2. Current tier is higher than last celebrated tier
    const workoutsIncreased = totalWorkouts > (previousTotalWorkoutsRef.current ?? 0);
    
    if (workoutsIncreased && currentTierIndex > lastCelebratedIndex) {
      // Tier increased from completing a workout - show celebration!
      setCelebrationTier(currentTier);
      setShowTierCelebration(true);
      setPreviousTier(currentTier);
      updateProfile({ lastCelebratedTier: currentTier });
    } else if (currentTier !== previousTier) {
      // Tier changed but we've already celebrated or workouts didn't increase
      setPreviousTier(currentTier);
    }
    
    // Update the ref for next comparison
    previousTotalWorkoutsRef.current = totalWorkouts;
  }, [totalWorkouts, previousTier, user?.id, profile.lastCelebratedTier, profileLoading, updateProfile]);

  // Sync user profile data to OneSignal tags for segmentation
  useEffect(() => {
    if (!user?.id || profileLoading) return;
    
    const tags: {
      tier?: string;
      goal?: string;
      experience?: string;
      workoutDays?: string;
    } = {};
    
    if (profile.tier) tags.tier = profile.tier;
    if (profile.stats?.goal) tags.goal = profile.stats.goal;
    if (profile.stats?.experience) tags.experience = profile.stats.experience;
    if (profile.stats?.daysPerWeek) tags.workoutDays = String(profile.stats.daysPerWeek);
    
    if (Object.keys(tags).length > 0) {
      updateUserTags(tags);
    }
  }, [user?.id, profile.tier, profile.stats?.goal, profile.stats?.experience, profile.stats?.daysPerWeek, profileLoading, updateUserTags]);

  const handleTourComplete = async () => {
    // Save to sessionStorage immediately as backup (prevents re-showing this session)
    sessionStorage.setItem('evolve_tour_completed_session', 'true');
    
    // Save tour completion to database
    if (user?.id) {
      await updateProfile({ hasSeenTour: true });
    }
    
    // Dismiss from prompt queue to allow next prompt
    dismissPrompt('tour');
    setTourHighlightedCard(null);
    setShowSettingsModal(false); // Close settings modal when tour ends
  };

  const handleTourSettingsToggle = (open: boolean) => {
    setShowSettingsModal(open);
  };

  // Calculate trial status from profile
  const getTrialStatus = (): 'none' | 'active' | 'expired' => {
    if (!profile.trialStartedAt) return 'none';
    const daysSinceTrialStart = Math.floor(
      (Date.now() - new Date(profile.trialStartedAt).getTime()) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceTrialStart >= TRIAL_DAYS) return 'expired';
    return 'active';
  };

  const trialStatus = getTrialStatus();
  const isInTrial = trialStatus === 'active';

  // Calculate actual days since joining (instead of using the database counter)
  const actualDaysActive = profile.createdAt 
    ? Math.max(1, Math.floor((Date.now() - new Date(profile.createdAt).getTime()) / (1000 * 60 * 60 * 24)))
    : 1;

  // Convert ExtendedProfile to UserProfile for components that need it
  // Use subscription.subscribed as source of truth for premium status (validated via Stripe/RevenueCat)
  // This ensures UI reflects actual subscription status, not potentially stale DB values
  const userProfileForComponents: UserProfile = {
    id: user?.id || '',
    name: profile.name,
    joinDate: profile.createdAt ? new Date(profile.createdAt) : new Date(),
    tier: profile.tier,
    daysActive: actualDaysActive,
    totalWorkouts: 0,
    isPremium: subscription.subscribed || profile.isPremium,
    coachingStyle: profile.coachingStyle,
    trialStatus,
    trialStartDate: profile.trialStartedAt ? new Date(profile.trialStartedAt) : undefined,
    hasCompletedOnboarding: onboardingComplete,
    avatarUrl: profile.avatarUrl,
  };

  // Build recent workout history for AI context (last 5 workouts with details)
  const recentWorkouts = workoutSessions.slice(0, 5).map(session => {
    // Safely handle exercise_data which may be stored as JSON string in DB
    const exerciseData = Array.isArray(session.exercise_data) ? session.exercise_data : [];
    return {
      date: session.completed_at,
      exercisesCompleted: session.exercises_completed,
      exercises: exerciseData.map(ex => ({
        name: ex.name,
        sets: ex.sets,
      })),
    };
  });

  // Calculate workout streak (consecutive days with at least one workout)
  const { currentStreak, lastWorkoutDaysAgo } = useMemo(() => {
    if (workoutSessions.length === 0) {
      return { currentStreak: 0, lastWorkoutDaysAgo: null };
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Get unique dates with workouts (normalized to start of day)
    const workoutDates = new Set<string>();
    workoutSessions.forEach(session => {
      const date = new Date(session.completed_at);
      date.setHours(0, 0, 0, 0);
      workoutDates.add(date.toISOString().split('T')[0]);
    });

    // Sort dates descending (most recent first)
    const sortedDates = Array.from(workoutDates).sort().reverse();
    
    if (sortedDates.length === 0) {
      return { currentStreak: 0, lastWorkoutDaysAgo: null };
    }

    // Calculate days since last workout
    const lastWorkoutDate = new Date(sortedDates[0]);
    const daysSinceLastWorkout = Math.floor((today.getTime() - lastWorkoutDate.getTime()) / (1000 * 60 * 60 * 24));

    // Calculate streak - count consecutive days starting from today or yesterday
    let streak = 0;
    let checkDate = new Date(today);
    
    // If no workout today, start checking from yesterday
    const todayStr = today.toISOString().split('T')[0];
    const hasWorkoutToday = workoutDates.has(todayStr);
    
    if (!hasWorkoutToday) {
      // Check if last workout was yesterday (streak still active)
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.toISOString().split('T')[0];
      
      if (!workoutDates.has(yesterdayStr)) {
        // No workout yesterday either - streak is broken
        return { currentStreak: 0, lastWorkoutDaysAgo: daysSinceLastWorkout };
      }
      checkDate = yesterday;
    }

    // Count consecutive days with workouts
    while (true) {
      const dateStr = checkDate.toISOString().split('T')[0];
      if (workoutDates.has(dateStr)) {
        streak++;
        checkDate.setDate(checkDate.getDate() - 1);
      } else {
        break;
      }
    }

    return { currentStreak: streak, lastWorkoutDaysAgo: daysSinceLastWorkout };
  }, [workoutSessions]);

  // Initialize AI chat hook with nutrition context
  const { streamChat, error: chatError } = useEvolveChat({
    coachingStyle: profile.coachingStyle,
    userContext: {
      name: profile.name,
      daysActive: profile.daysActive,
      tier: profile.tier,
      prs: profile.prs,
      motivation: userMotivation || profile.motivation,
      stats: profile.stats,
      isPremium: profile.isPremium,
      totalWorkouts,
      recentWorkouts,
      currentStreak,
      lastWorkoutDaysAgo: lastWorkoutDaysAgo ?? undefined,
      nutrition: todayNutrition && profile.nutritionTargets ? {
        dailyCalorieTarget: profile.nutritionTargets.calories,
        proteinTargetG: profile.nutritionTargets.protein,
        carbsTargetG: profile.nutritionTargets.carbs,
        fatTargetG: profile.nutritionTargets.fat,
        todayCalories: todayNutrition.totalCalories,
        todayProtein: todayNutrition.totalProtein,
        todayCarbs: todayNutrition.totalCarbs,
        todayFat: todayNutrition.totalFat,
        caloriesRemaining: (profile.nutritionTargets.calories || 0) - todayNutrition.totalCalories,
        proteinRemaining: (profile.nutritionTargets.protein || 0) - todayNutrition.totalProtein,
      } : undefined,
    },
  });

  // Show chat errors
  useEffect(() => {
    if (chatError) {
      toast({
        title: "Chat Error",
        description: chatError,
        variant: "destructive",
      });
    }
  }, [chatError, toast]);

  // Check trial expiration
  useEffect(() => {
    if (trialStatus === 'expired' && !profile.isPremium) {
      toast({
        title: "Trial Ended",
        description: "Your 3-day free trial has ended. Upgrade to Premium for unlimited access!",
      });
    }
  }, [trialStatus, profile.isPremium, toast]);

  // Refetch profile when subscription status changes to subscribed
  useEffect(() => {
    if (subscription.subscribed && !profile.isPremium) {
      console.log('[Index] Subscription detected, refetching profile to update premium status');
      refetchProfile();
      // Close paywall and show confetti
      setShowPaywall(false);
      setShowPremiumConfetti(true);
      toast({
        title: "🎉 Welcome to Premium!",
        description: "You now have unlimited access. Let's crush it!",
      });
    }
  }, [subscription.subscribed, profile.isPremium, refetchProfile, toast]);

  const handleOnboardingComplete = async (
    coachingStyle: CoachingStyle, 
    selectedTheme: AppTheme, 
    motivation?: string,
    stats?: { 
      age?: number; 
      sex?: string;
      height?: string;
      weight?: number; 
      goal?: string; 
      specificGoal?: string;
      experience?: string;
      daysPerWeek?: number;
      equipment?: string;
      injuries?: string;
      dietPreferences?: string;
      displayName?: string;
      leaderboardPrivacy?: string;
    }
  ) => {
    // Save theme
    updateTheme(selectedTheme);
    
    // Save motivation
    if (motivation) {
      setUserMotivation(motivation);
    }
    
    // Save profile with coaching style and stats
    await updateProfile({
      coachingStyle,
      trialStartedAt: new Date().toISOString(),
      onboardingComplete: true,
      motivation,
      stats,
      displayName: stats?.displayName,
      leaderboardPrivacy: (stats?.leaderboardPrivacy as 'full_name' | 'nickname' | 'anonymous' | 'hidden') || 'full_name',
    });

    // Add initial AI message with personalized greeting based on coaching style
    const baseMessage = getPersonalizedInitialMessage(profile.name || 'champion', coachingStyle);
    const personalizedMessage = motivation 
      ? baseMessage + `\n\nI see you're doing this for "${motivation}" — that's the kind of fuel that builds legends. Let's get started.`
      : baseMessage;
      
    // Save initial AI message to database
    await saveMessage(personalizedMessage, true);

    toast({
      title: "🔥 Trial Started!",
      description: "You have 3 days of unlimited access. Let's evolve!",
    });

    // Queue auto-generation of first workout plan
    setTimeout(() => {
      setPendingAutoMessage("Create my first workout plan based on my goals and equipment.");
    }, 2000);

    // Health connection prompt is now handled by the prompt queue automatically
    // No need to manually trigger it here
  };

  const handleCoachingStyleChange = async (style: CoachingStyle) => {
    await updateProfile({ coachingStyle: style });
    toast({
      title: "Coach Updated",
      description: "Your coach personality has been changed.",
    });
  };

  const handleEnableNotifications = async () => {
    const enabled = await requestNotifications();
    dismissPrompt('notifications');
    if (enabled) {
      toast({
        title: "🔔 Notifications Enabled!",
        description: "You're 4.7× more likely to hit Immortal now.",
      });
    }
  };

  const handleSendMessage = useCallback(async (text: string) => {
    // Check AI consent first - block all AI features without consent
    if (!canUseAI()) {
      requestAIConsent();
      return;
    }
    
    const isTrialExpired = trialStatus === 'expired';
    
    // Show paywall at 10 messages or trial expired
    if (!profile.isPremium && !isInTrial && messagesUsed >= DAILY_MESSAGE_LIMIT) {
      setShowPaywall(true);
      return;
    }

    if (isTrialExpired && !profile.isPremium) {
      setShowPaywall(true);
      return;
    }

    // Check for PRs mentioned in the user's message and sync them
    const detectedPRs = findPRsToSync(text, profile.prs);
    if (detectedPRs.length > 0) {
      const newPRs: PR[] = detectedPRs.map(pr => ({
        id: Date.now().toString() + '-' + pr.lift,
        lift: pr.lift,
        weight: pr.weight,
        date: new Date(),
      }));
      const updatedPrs = [...newPRs, ...profile.prs];
      await updateProfile({ prs: updatedPrs });
      
      // Notify user that PRs were synced
      if (detectedPRs.length === 1) {
        toast({
          title: "🏆 PR Auto-Logged!",
          description: `${detectedPRs[0].lift}: ${detectedPRs[0].weight} lbs synced to Progress!`,
        });
      } else {
        toast({
          title: "🏆 PRs Auto-Logged!",
          description: `${detectedPRs.length} new PRs synced to Progress!`,
        });
      }
    }

    // Check for workout descriptions and notify
    const detectedWorkout = detectWorkoutFromMessage(text);
    if (detectedWorkout) {
      toast({
        title: "💪 Workout Detected!",
        description: `Logged: ${formatDetectedWorkout(detectedWorkout)}`,
      });
    }

    // Check for body weight updates in the message
    const detectedWeight = detectWeightFromMessage(text);
    console.log('Weight detection result:', detectedWeight, 'Current weight:', profile.stats?.weight);
    
    if (detectedWeight && isWeightChanged(detectedWeight, profile.stats?.weight)) {
      // Auto-update with high or medium confidence
      if (detectedWeight.confidence === 'high' || detectedWeight.confidence === 'medium') {
        // Log to weight history
        await logWeight(detectedWeight.weight);
        
        // Update profile stats
        await updateProfile({ 
          stats: { ...profile.stats, weight: detectedWeight.weight } 
        });

        // Sync to Apple Health / Health Connect if enabled
        if (healthStatus.isAuthorized && profile.healthSyncWeight) {
          const synced = await syncWeightToHealth(detectedWeight.weight);
          if (synced) {
            toast({
              title: `Synced to ${platformName}!`,
              description: `${detectedWeight.weight} lbs saved to Health app`,
            });
          }
        }
        
        toast({
          title: "⚖️ Weight Updated!",
          description: `Auto-logged ${detectedWeight.weight} lbs from your message`,
        });
      }
    }

    // Save user message to database
    const savedUserMsg = await saveMessage(text, false);
    if (!savedUserMsg) return;
    
    if (!isInTrial) {
      setMessagesUsed(prev => prev + 1);
    }
    setIsLoading(true);

    // Create placeholder AI message
    const aiMessageId = (Date.now() + 1).toString();
    const placeholderAiMessage: ChatMessage = {
      id: aiMessageId,
      text: '',
      isAI: true,
      createdAt: new Date(),
    };
    setMessages(prev => [...prev, placeholderAiMessage]);

    // Stream AI response
    let assistantContent = "";

    await streamChat(
      [...messages, savedUserMsg],
      (chunk) => {
        assistantContent += chunk;
        setMessages(prev => prev.map(m => 
          m.id === aiMessageId ? { ...m, text: assistantContent } : m
        ));
      },
      async () => {
        // Save final AI message to database
        if (assistantContent) {
          await saveMessage(assistantContent, true);
          // Update local state to use the saved message
          setMessages(prev => prev.filter(m => m.id !== aiMessageId));
          
          // Auto-detect nutrition from conversation (user + AI) and sync to Progress
          const detectedNutrition = detectNutritionFromConversation(text, assistantContent);
          if (detectedNutrition) {
            const logged = await logNutrition({
              calories: detectedNutrition.calories,
              protein: detectedNutrition.protein,
              carbs: detectedNutrition.carbs,
              fat: detectedNutrition.fat,
              mealType: detectedNutrition.mealType,
              notes: detectedNutrition.foodDescription,
            });
            if (logged) {
              toast({
                title: "🍎 Nutrition Logged!",
                description: `${detectedNutrition.calories} cal synced to Progress`,
              });
            }
          }
        }
        setIsLoading(false);
      }
    );
  }, [messages, profile, messagesUsed, streamChat, isInTrial, trialStatus, saveMessage, updateProfile, logNutrition, toast, canUseAI, requestAIConsent]);

  // Process pending auto-message (e.g., auto-generate workout after onboarding)
  const processedAutoMessageRef = useRef<string | null>(null);
  useEffect(() => {
    if (pendingAutoMessage && !isLoading && pendingAutoMessage !== processedAutoMessageRef.current) {
      processedAutoMessageRef.current = pendingAutoMessage;
      handleSendMessage(pendingAutoMessage);
      setPendingAutoMessage(null);
    }
  }, [pendingAutoMessage, isLoading, handleSendMessage]);

  const handleAddPR = async (lift: string, weight: number) => {
    const newPR: PR = {
      id: Date.now().toString(),
      lift,
      weight,
      date: new Date(),
    };
    const updatedPrs = [newPR, ...profile.prs];
    await updateProfile({ prs: updatedPrs });
    toast({
      title: "🏆 New PR Logged!",
      description: `${lift}: ${weight} lbs - Keep pushing, Evolver!`,
    });
  };

  const handleUpgrade = () => {
    // Use subscription.subscribed (validated via Stripe/RevenueCat) as source of truth
    // This is more reliable than profile.isPremium which could be stale
    if (subscription.subscribed) {
      toast({
        title: "✨ You already have Premium!",
        description: "Enjoy unlimited access to all Evolve features.",
        duration: 3000,
      });
      return;
    }
    setShowPaywall(true);
  };

  const handleEditPR = (pr: PR) => {
    setEditingPR(pr);
    setShowPRModal(true);
  };

  const handleUpdatePR = (id: string, newWeight: number) => {
    const updatedPrs = profile.prs.map(pr => 
      pr.id === id ? { ...pr, weight: newWeight, date: new Date() } : pr
    );
    updateProfile({ prs: updatedPrs });
    toast({
      title: "PR Updated!",
      description: `Weight updated to ${newWeight} lbs`,
    });
  };

  const handleDeletePR = (id: string) => {
    const updatedPrs = profile.prs.filter(pr => pr.id !== id);
    updateProfile({ prs: updatedPrs });
    toast({
      title: "PR Deleted",
      description: "Personal record removed",
    });
  };

  const handleWeightUpdate = async (newWeight: number) => {
    // Log weight to history
    await logWeight(newWeight);
    
    // Update profile stats
    await updateProfile({ 
      stats: { ...profile.stats, weight: newWeight } 
    });

    // Sync to Apple Health / Health Connect if enabled
    if (healthStatus.isAuthorized && profile.healthSyncWeight) {
      const synced = await syncWeightToHealth(newWeight);
      if (synced) {
        toast({
          title: `Synced to ${platformName}!`,
          description: `${newWeight} lbs saved to Health app`,
        });
      }
    }

    toast({
      title: "Weight Updated!",
      description: `Logged ${newWeight} lbs`,
    });

    // Send a message to the chat so the coach notices
    const weightMessage = `I just updated my weight to ${newWeight} lbs`;
    handleSendMessage(weightMessage);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  // Loading state - only check profile loading since auth is handled by ProtectedRoute
  if (profileLoading && !profileError) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 animate-in fade-in duration-300">
        <div className="w-16 h-16 rounded-full evolve-gradient flex items-center justify-center evolve-glow">
          <Loader2 className="w-8 h-8 animate-spin text-foreground" />
        </div>
        <p className="text-muted-foreground text-sm">Loading your training...</p>
      </div>
    );
  }

  // Show error recovery UI for authenticated users with profile errors
  if (user && profileError) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6 animate-in fade-in duration-300">
        <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
          <span className="text-3xl">⚠️</span>
        </div>
        <h2 className="text-xl font-semibold text-foreground">Something went wrong</h2>
        <p className="text-muted-foreground text-center max-w-sm">
          We couldn't load your profile. This sometimes happens after updates.
        </p>
        <div className="flex flex-col gap-3 w-full max-w-xs mt-4">
          <button
            onClick={() => refetchProfile()}
            className="w-full py-3 px-4 rounded-lg bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
          >
            Try Again
          </button>
          <button
            onClick={handleLogout}
            className="w-full py-3 px-4 rounded-lg bg-secondary text-secondary-foreground font-medium hover:bg-secondary/80 transition-colors"
          >
            Log Out & Sign In Again
          </button>
        </div>
        <p className="text-xs text-muted-foreground mt-4 text-center">
          If this keeps happening, try logging out and back in.
        </p>
      </div>
    );
  }

  // Show onboarding for new users
  if (!onboardingComplete) {
    return (
      <Onboarding 
        onComplete={handleOnboardingComplete}
        onRequestNotifications={requestNotifications}
      />
    );
  }

  const trialDaysLeft = isInTrial && profile.trialStartedAt 
    ? TRIAL_DAYS - Math.floor((Date.now() - new Date(profile.trialStartedAt).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className="h-screen bg-background flex flex-col overflow-hidden">
      {/* Despia safe area spacer - top */}
      <div className="safe-area-top" />
      
      <EvolveHeader 
        userName={profile.name} 
        totalWorkouts={totalWorkouts}
        onSettingsClick={() => setShowSettingsModal(true)}
        onLeaderboardClick={() => setShowLeaderboard(true)}
      />
      
      <main className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Notification prompt banner - controlled by prompt queue */}
        {isPromptActive('notifications') && activeTab === 'chat' && (
          <div className="px-4 pt-2">
            <NotificationPrompt
              onEnable={handleEnableNotifications}
              onDismiss={() => dismissPrompt('notifications')}
              isUpsell
            />
          </div>
        )}

        {/* Resume workout banner - shown on all tabs */}
        {!showWorkoutSession && (
          <ResumeWorkoutBanner
            onResume={() => setShowWorkoutSession(true)}
            onDiscard={() => {}}
          />
        )}

        {activeTab === 'chat' && (
          <ChatTab
            messages={messages}
            onSend={handleSendMessage}
            isLoading={isLoading}
            messagesUsed={messagesUsed}
            messageLimit={DAILY_MESSAGE_LIMIT}
            isPremium={profile.isPremium || isInTrial}
            isInTrial={isInTrial}
            trialDaysLeft={trialDaysLeft}
            onLoadMore={loadMore}
            hasMore={hasMore}
            onSearch={searchMessages}
            botIcon={theme.botIcon}
            userId={user?.id}
            hasWorkoutButton={!!workoutPlan}
            weightUnit={profile.weightUnit}
            hasAIConsent={hasAIConsent === true}
            onRequestConsent={requestAIConsent}
            consentDeniedMessage={consentDeniedMessage}
            onDismissDeniedMessage={dismissDeniedMessage}
          />
        )}
        {activeTab === 'progress' && (
          <ProgressTab
            prs={profile.prs}
            onAddPR={(liftName?: string) => {
              setEditingPR(null);
              setPreselectedLift(liftName || null);
              setShowPRModal(true);
            }}
            onEditPR={handleEditPR}
            chatMessages={messages}
            chatLoading={chatHistoryLoading}
            isPremium={profile.isPremium}
            onUpgrade={handleUpgrade}
            onStartWorkout={(planText) => {
              setCurrentWorkoutPlan(planText);
              setShowWorkoutSession(true);
              startWorkoutTracking(); // Start tracking for reminders
            }}
            weightHistory={weightHistory.map(w => ({ date: w.logged_at, weight: w.weight }))}
            weightHistoryFull={weightHistory}
            totalWorkouts={totalWorkouts}
            currentWeight={profile.stats?.weight}
            goalWeight={profile.stats?.goalWeight}
            onWeightUpdate={handleWeightUpdate}
            onDeleteWeight={deleteWeightEntry}
            isBadgeUnlocked={isBadgeUnlocked}
            getBadgeUnlockDate={getBadgeUnlockDate}
            onBadgeClick={setSelectedBadge}
            highlightedCard={tourHighlightedCard}
            workoutSessions={workoutSessions}
            // Health sync props
            isHealthAvailable={isHealthAvailable}
            isHealthConnected={healthStatus.isAuthorized && profile.healthSyncEnabled}
            healthPlatformName={isHealthAvailable ? (healthStatus.platform === 'apple' ? 'Apple Health' : 'Health Connect') : 'Health'}
            onConnectHealth={() => {
              // Clear any dismissed flags so the prompt shows
              localStorage.removeItem('health_prompted_onboarding');
              localStorage.removeItem('health_permanently_denied');
              forceShowPrompt('health_onboarding');
            }}
            readSteps={readSteps}
            readHeartRate={readHeartRate}
            readActiveEnergy={readActiveEnergy}
            readWeight={readWeightFromHealth}
            onLogNutrition={async (entry) => {
              // Log to database
              const logged = await logNutrition({
                calories: entry.calories,
                protein: entry.protein,
                carbs: entry.carbs,
                fat: entry.fat,
                mealType: entry.meal,
              });
              // Also sync to Health if connected
              if (logged && healthStatus.isAuthorized && profile.healthSyncEnabled) {
                await syncNutritionToHealth(entry.calories, entry.protein, entry.carbs, entry.fat);
              }
              return logged;
            }}
            onImportFromHealth={async () => {
              const healthWeight = await readWeightFromHealth(30);
              if (healthWeight && healthWeight.length > 0) {
                const imported = await importFromHealth(healthWeight, 'lbs');
                if (imported > 0) {
                  toast({ title: `Imported ${imported} weight entries from Health` });
                } else {
                  toast({ title: 'No new weight data to import' });
                }
              }
            }}
            // Nutrition goals & progress
            nutritionTargets={profile.nutritionTargets}
            todayNutrition={todayNutrition ? {
              calories: todayNutrition.totalCalories,
              protein: todayNutrition.totalProtein,
              carbs: todayNutrition.totalCarbs,
              fat: todayNutrition.totalFat,
            } : undefined}
            weekNutrition={weekNutrition}
            userStats={{
              weight: profile.stats?.weight,
              height: profile.stats?.height,
              age: profile.stats?.age,
              sex: profile.stats?.sex,
              activityLevel: profile.activityLevel,
            }}
            onSaveNutritionGoals={async (targets) => {
              await updateProfile({
                nutritionTargets: targets,
              });
            }}
            // Meal history props
            nutritionLogs={weekNutrition?.flatMap(day => day.logs) || []}
            onRepeatMeal={async (log) => {
              return await logNutrition({
                calories: log.calories,
                protein: log.protein_g || undefined,
                carbs: log.carbs_g || undefined,
                fat: log.fat_g || undefined,
                mealType: log.meal_type || undefined,
                notes: log.notes || undefined,
              });
            }}
            onDeleteNutritionLog={deleteNutritionLog}
          />
        )}
        {activeTab === 'profile' && (
          <ProfileTab
            profile={userProfileForComponents}
            onUpgrade={handleUpgrade}
            onLogout={handleLogout}
            onOpenSettings={() => setShowSettingsModal(true)}
            onOpenLeaderboard={() => setShowLeaderboard(true)}
            onManageSubscription={() => setShowPremiumStatusModal(true)}
            onOpenReferral={() => setShowReferralModal(true)}
            onReplayTour={() => forceShowPrompt('tour')}
            isEarlyBird={subscription.isEarlyBird}
            totalWorkouts={totalWorkouts}
            onAvatarUpdate={(url) => updateProfile({ avatarUrl: url })}
            subscriptionEnd={subscription.subscriptionEnd}
            subscriptionPlan={subscription.plan}
          />
        )}
      </main>

      <BottomNav 
        activeTab={activeTab} 
        onTabChange={setActiveTab}
        hasWorkoutPlan={!!workoutPlan}
        isWorkoutActive={showWorkoutSession}
        onGoWorkout={() => {
          if (workoutPlan) {
            setCurrentWorkoutPlan(workoutPlan);
            setShowWorkoutSession(true);
            setTourHighlightedCard(null);
            startWorkoutTracking(); // Start tracking for reminders
          }
        }}
      />
      
      <AddPRModal
        isOpen={showPRModal}
        onClose={() => {
          setShowPRModal(false);
          setEditingPR(null);
          setPreselectedLift(null);
        }}
        onAdd={handleAddPR}
        onUpdate={handleUpdatePR}
        onDelete={handleDeletePR}
        editingPR={editingPR}
        preselectedLift={preselectedLift}
      />

      <SettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        userId={user?.id}
        userName={profile.name}
        avatarUrl={profile.avatarUrl}
        onAvatarChange={(url) => updateProfile({ avatarUrl: url })}
        coachingStyle={profile.coachingStyle}
        onCoachingStyleChange={handleCoachingStyleChange}
        theme={theme}
        onThemeChange={updateTheme}
        onThemeReset={resetTheme}
        notificationsEnabled={notificationsEnabled}
        onEnableNotifications={handleEnableNotifications}
        userStats={profile.stats}
        onStatsChange={async (stats) => {
          await updateProfile({ stats });
        }}
        onReplayTour={() => forceShowPrompt('tour')}
        referralCode={profile.referralCode}
        isEvolverFounder={profile.isEvolverFounder}
        displayName={profile.displayName}
        leaderboardPrivacy={profile.leaderboardPrivacy}
        onPrivacyChange={(displayName, privacy) => {
          updateProfile({ displayName, leaderboardPrivacy: privacy });
        }}
        notificationPreferences={profile.notificationPreferences}
        onNotificationPreferencesChange={(prefs) => {
          updateProfile({ notificationPreferences: prefs });
        }}
        onRefreshSubscription={async () => {
          await checkSubscription();
          await refetchProfile();
          toast({
            title: "Subscription Refreshed",
            description: subscription.subscribed ? "Your premium status is active!" : "No active subscription found.",
          });
        }}
        subscriptionLoading={subscriptionLoading}
      />

      {/* Premium Confetti */}
      <PremiumConfetti trigger={showPremiumConfetti} />

      {/* Workout Session */}
      <WorkoutSession
        isOpen={showWorkoutSession}
        onClose={() => setShowWorkoutSession(false)}
        onCancel={completeWorkoutTracking} // Clear tracking when canceled
        planText={currentWorkoutPlan}
        existingPRs={profile.prs.map(pr => ({ lift: pr.lift, weight: pr.weight }))}
        weightUnit={profile.weightUnit}
        onNewPR={async (lift, weight) => {
          const newPR: PR = {
            id: Date.now().toString(),
            lift,
            weight,
            date: new Date(),
          };
          await updateProfile({ prs: [newPR, ...profile.prs] });
        }}
        onComplete={async (session) => {
          // Clear workout tracking (stops reminder notifications)
          completeWorkoutTracking();
          
          // Build detailed exercise data for storage (including notes)
          const completedExercises = session.exercises.filter(e => e.completed && !e.skipped);
          const exerciseData = completedExercises.map(ex => ({
            name: ex.name,
            sets: ex.actualSets?.filter(s => s.completed).map(s => ({
              reps: s.reps,
              weight: s.weight,
              notes: s.notes, // Include set-level notes
            })) || [],
            completed: ex.completed,
            skipped: ex.skipped,
          }));
          
          // Collect all notes from the workout (from individual sets)
          const allNotes: string[] = [];
          completedExercises.forEach(ex => {
            ex.actualSets?.filter(s => s.completed && s.notes?.trim()).forEach(s => {
              allNotes.push(`${ex.name}: ${s.notes}`);
            });
          });
          
          // Log the workout session to the database with full details
          const workoutNotes = allNotes.length > 0 ? allNotes.join('\n') : undefined;
          const logResult = await logWorkout(completedExercises.length, workoutNotes, exerciseData);
          
          if (logResult) {
            // Increment days active (workouts tracked)
            const newDaysActive = profile.daysActive + 1;
            await updateProfile({ daysActive: newDaysActive });
            
            toast({
              title: "🔥 Workout Logged!",
              description: `You've now completed ${newDaysActive} workouts!`,
            });

            // Sync workout to Apple Health / Health Connect if enabled
            if (healthStatus.isAuthorized && profile.healthSyncWorkouts) {
              // Use actual session duration if available, otherwise estimate
              const actualDuration = session.duration || Math.max(15, completedExercises.length * 4);
              // Estimate calories based on duration (rough: 5-7 cal per minute for strength training)
              const estimatedCalories = Math.round(actualDuration * 6);
              
              const synced = await syncWorkoutToHealth('strength', actualDuration, estimatedCalories);
              if (synced) {
                toast({
                  title: `Synced to ${platformName}!`,
                  description: `${actualDuration} min workout saved to Health app`,
                });
              }
            }

            // Health connection prompt after first workout is now handled by prompt queue
            // No need to manually trigger it here
          }
          
          // Build rich workout summary for chat (including notes)
          const exerciseSummary = completedExercises.map(ex => {
            const sets = ex.actualSets?.filter(s => s.completed) || [];
            if (sets.length === 0) return ex.name;
            const setDetails = sets.map(s => {
              let detail = `${s.weight}lbs x ${s.reps}`;
              if (s.notes?.trim()) {
                detail += ` (${s.notes.trim()})`;
              }
              return detail;
            }).join(', ');
            return `${ex.name}: ${setDetails}`;
          }).join('\n• ');
          
          // Build notes section for chat if there are notes
          const notesSection = allNotes.length > 0 
            ? `\n\n📝 Notes:\n${allNotes.join('\n')}` 
            : '';
          
          if (exerciseSummary) {
            // Send detailed workout data to AI with notes
            await handleSendMessage(
              `Just finished my workout!\n\n• ${exerciseSummary}${notesSection}`
            );
          }
        }}
      />

      {/* Paywall Modal */}
      {showPaywall && (
        <PaywallModal
          userName={profile.name}
          motivation={userMotivation}
          isTrialExpired={trialStatus === 'expired'}
          loading={subscriptionLoading || platformPurchaseLoading}
          isEmailVerified={isEmailVerified}
          userEmail={user?.email}
          earlyBirdSpotsLeft={(() => {
            const storageKey = 'evolve_early_bird_spots';
            let spots = parseInt(localStorage.getItem(storageKey) || '137');
            if (!localStorage.getItem(storageKey)) {
              localStorage.setItem(storageKey, '137');
            } else {
              // Decrease by 1-2 each time, minimum 7
              const decrease = Math.floor(Math.random() * 2) + 1;
              spots = Math.max(7, spots - decrease);
              localStorage.setItem(storageKey, spots.toString());
            }
            return spots;
          })()}
          onUpgrade={async (plan: PricingPlan) => {
            try {
              // Use platform-aware purchase - RevenueCat on iOS, Stripe on web/Android
              const success = await platformPurchase(plan);
              if (success) {
                setShowPaywall(false);
                // Trigger celebration immediately on successful purchase
                setShowPremiumConfetti(true);
                toast({
                  title: "🎉 Welcome to Premium!",
                  description: "You now have unlimited access. Let's crush it!",
                });
                // Refresh subscription status in background
                checkSubscription();
                refetchProfile();
              }
            } catch (error) {
              console.error('[Index] Upgrade error:', error);
              toast({
                title: "Upgrade Error",
                description: "Something went wrong. Please try again.",
                variant: "destructive",
              });
            }
          }}
          onClose={() => setShowPaywall(false)}
        />
      )}

      {/* Leaderboard Modal */}
      <LeaderboardModal
        isOpen={showLeaderboard}
        onClose={() => setShowLeaderboard(false)}
        currentUserId={user?.id}
        currentTotalWorkouts={totalWorkouts}
        savedWeightUnit={profile.weightUnit}
        onUpdateWeightUnit={(unit) => updateProfile({ weightUnit: unit })}
      />

      {/* Tier Up Celebration */}
      <TierUpCelebration
        isOpen={showTierCelebration}
        onClose={() => setShowTierCelebration(false)}
        newTier={celebrationTier}
        totalWorkouts={totalWorkouts}
        userName={profile.name}
        referralCode={profile.referralCode}
      />

      {/* Onboarding Tour - controlled by prompt queue */}
      <OnboardingTour
        isOpen={isPromptActive('tour')}
        onClose={handleTourComplete}
        onComplete={handleTourComplete}
        onTabChange={setActiveTab}
        onHighlightChange={setTourHighlightedCard}
        onSettingsToggle={handleTourSettingsToggle}
        onWorkoutStart={() => {
          setShowWorkoutSession(true);
        }}
        onWorkoutClose={() => {
          setShowWorkoutSession(false);
        }}
        hasWorkoutPlan={!!workoutPlan}
      />

      {/* Referral Notification Modal */}
      <ReferralNotificationModal
        notification={currentNotification}
        onClose={dismissCurrent}
      />

      {/* Badge Unlock Modal */}
      <BadgeUnlockModal
        badge={newlyUnlocked}
        userName={profile.name}
        daysActive={actualDaysActive}
        tier={profile.tier}
        referralCode={profile.referralCode}
        onClose={dismissUnlock}
      />

      {/* Badge Detail Modal (when clicking unlocked badge) */}
      <BadgeUnlockModal
        badge={selectedBadge}
        userName={profile.name}
        daysActive={actualDaysActive}
        tier={profile.tier}
        referralCode={profile.referralCode}
        onClose={() => setSelectedBadge(null)}
      />

      {/* Referral Modal */}
      {user && (
        <ReferralModal
          isOpen={showReferralModal}
          onClose={() => setShowReferralModal(false)}
          userId={user.id}
          referralCode={profile.referralCode}
          isEvolverFounder={profile.isEvolverFounder}
        />
      )}

      {/* Health Connection Prompt - shows on first launch after onboarding */}
      <HealthConnectionPrompt
        isOpen={isPromptActive('health_onboarding')}
        onClose={() => dismissPrompt('health_onboarding')}
        userId={user?.id}
        trigger="onboarding"
      />

      {/* Premium Status Modal */}
      <PremiumStatusModal
        isOpen={showPremiumStatusModal}
        onClose={() => setShowPremiumStatusModal(false)}
        subscriptionPlan={subscription.plan}
        onManageSubscription={openCustomerPortal}
        isIOSPurchase={subscription.isIOSPurchase}
      />

      {/* AI Consent Modal - controlled by prompt queue OR manual request */}
      <AIConsentModal
        open={isPromptActive('ai_consent') || needsConsentPrompt}
        onConsent={() => {
          grantAIConsent();
          dismissPrompt('ai_consent');
          clearConsentPrompt();
        }}
        onDecline={() => {
          declineAIConsent();
          dismissPrompt('ai_consent');
          clearConsentPrompt();
        }}
      />

    </div>
  );
}
